import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, TextInput, ScrollView, Platform, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { DEFAULT_TOS, DEFAULT_PRIVACY } from '../constants/legalDefaults';
import { uiVar } from './floodlight/uiStyleCss';
import { ADM_DISPLAY } from './floodlight/vars/admin';

const showToast = (text, type = 'info') => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('toast', { detail: { type, text } }));
  }
};

const DOC_REF = doc(db, 'site_config', 'legal_content');

const SECTIONS = [
  { key: 'tos',     label: 'Terms of Service', icon: 'document-text-outline',    default: DEFAULT_TOS },
  { key: 'privacy', label: 'Privacy Policy',   icon: 'shield-checkmark-outline', default: DEFAULT_PRIVACY },
];

export default function LegalContentTab() {
  const [tos, setTos] = useState('');
  const [privacyPolicy, setPrivacyPolicy] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('tos');
  const [dirty, setDirty] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);

  // Use onSnapshot so changes from other sessions appear live
  useEffect(() => {
    const unsub = onSnapshot(
      DOC_REF,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          // Use Firestore content if present, otherwise fall back to defaults
          setTos(data.tos && data.tos.trim() ? data.tos : DEFAULT_TOS);
          setPrivacyPolicy(data.privacyPolicy && data.privacyPolicy.trim() ? data.privacyPolicy : DEFAULT_PRIVACY);
          if (data.lastUpdated?.toDate) setLastSaved(data.lastUpdated.toDate());
        } else {
          // Doc doesn't exist yet — pre-populate with defaults so admin sees real content
          setTos(DEFAULT_TOS);
          setPrivacyPolicy(DEFAULT_PRIVACY);
        }
        setLoading(false);
        setDirty(false);
      },
      () => {
        setTos(DEFAULT_TOS);
        setPrivacyPolicy(DEFAULT_PRIVACY);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await setDoc(DOC_REF, {
        tos,
        privacyPolicy,
        lastUpdated: serverTimestamp(),
      }, { merge: true });
      setDirty(false);
      showToast('Legal content saved — live immediately', 'success');
    } catch (e) {
      showToast('Failed to save: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  }, [tos, privacyPolicy]);

  const resetToDefault = useCallback(() => {
    if (activeSection === 'tos') { setTos(DEFAULT_TOS); setDirty(true); }
    else { setPrivacyPolicy(DEFAULT_PRIVACY); setDirty(true); }
    showToast('Reset to default — save to publish', 'info');
  }, [activeSection]);

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#a78bfa" />
        <Text style={styles.loadingText}>Loading legal content...</Text>
      </View>
    );
  }

  const currentText = activeSection === 'tos' ? tos : privacyPolicy;
  const currentSetter = activeSection === 'tos' ? setTos : setPrivacyPolicy;

  return (
    <View style={styles.wrap}>
      {/* Header */}
      <View style={styles.header}>
        <View style={{ flex: 1 }}>
          <Text style={styles.title}>Legal Content</Text>
          <Text style={styles.subtitle}>
            Edit Terms of Service and Privacy Policy. Changes go live immediately after saving.
            {lastSaved ? `  Last saved: ${lastSaved.toLocaleString()}` : ''}
          </Text>
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity style={styles.resetBtn} onPress={resetToDefault}>
            <Ionicons name="refresh-outline" size={14} color="rgba(255,255,255,0.5)" />
            <Text style={styles.resetBtnText}>Reset to default</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.previewToggleBtn, showPreview && styles.previewToggleBtnActive]}
            onPress={() => setShowPreview(v => !v)}
          >
            <Ionicons name={showPreview ? 'eye' : 'eye-off-outline'} size={14} color={showPreview ? uiVar('adm-accent', '#a78bfa') : 'rgba(255,255,255,0.5)'} />
            <Text style={[styles.previewToggleText, showPreview && { color: uiVar('adm-accent', '#a78bfa') }]}>Preview</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.saveBtn, (saving || !dirty) && styles.saveBtnDisabled]}
            onPress={handleSave}
            disabled={saving || !dirty}
          >
            {saving ? (
              <ActivityIndicator size="small" color="#000" />
            ) : (
              <>
                <Ionicons name="cloud-upload-outline" size={15} color={dirty ? '#000' : 'rgba(0,0,0,0.4)'} />
                <Text style={[styles.saveBtnText, !dirty && { color: 'rgba(0,0,0,0.4)' }]}>
                  {dirty ? 'Save & Publish' : 'Saved'}
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>

      {dirty && (
        <View style={styles.dirtyBanner}>
          <Ionicons name="alert-circle-outline" size={14} color="#f59e0b" />
          <Text style={styles.dirtyText}>Unsaved changes — users still see the previous version</Text>
        </View>
      )}

      {/* Section tabs */}
      <View style={styles.tabBar}>
        {SECTIONS.map(s => (
          <TouchableOpacity
            key={s.key}
            style={[styles.tab, activeSection === s.key && styles.tabActive]}
            onPress={() => setActiveSection(s.key)}
          >
            <Ionicons name={s.icon} size={15} color={activeSection === s.key ? uiVar('adm-accent', '#a78bfa') : 'rgba(255,255,255,0.5)'} />
            <Text style={[styles.tabText, activeSection === s.key && styles.tabTextActive]}>{s.label}</Text>
            <Text style={styles.tabCharCount}>
              {(activeSection === s.key ? currentText : (s.key === 'tos' ? tos : privacyPolicy)).length.toLocaleString()} chars
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Editor + Preview */}
      <View style={[styles.editorRow, !showPreview && { flexDirection: 'column' }]}>
        {/* Editor pane */}
        <View style={[styles.pane, showPreview && styles.paneHalf]}>
          <View style={styles.paneHeader}>
            <Ionicons name="create-outline" size={13} color="rgba(255,255,255,0.35)" />
            <Text style={styles.paneLabel}>Editor</Text>
          </View>
          <TextInput
            style={styles.editor}
            value={currentText}
            onChangeText={(val) => { currentSetter(val); setDirty(true); }}
            multiline
            textAlignVertical="top"
            placeholder="Start typing..."
            placeholderTextColor="rgba(255,255,255,0.15)"
            scrollEnabled
          />
        </View>

        {/* Preview pane */}
        {showPreview && (
          <View style={[styles.pane, styles.paneHalf]}>
            <View style={styles.paneHeader}>
              <Ionicons name="eye-outline" size={13} color="rgba(255,255,255,0.35)" />
              <Text style={styles.paneLabel}>Preview — exactly as users see it</Text>
            </View>
            <ScrollView style={styles.previewScroll} showsVerticalScrollIndicator>
              <Text style={styles.previewText}>{currentText}</Text>
              <View style={{ height: 40 }} />
            </ScrollView>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { flex: 1, padding: 24, display: 'flex', flexDirection: 'column' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: 12 },
  loadingText: { color: 'rgba(255,255,255,0.5)', fontSize: 13 },

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
    flexWrap: 'wrap',
    gap: 12,
  },
  title: { ...ADM_DISPLAY, letterSpacing: uiVar('adm-display-track', 'normal'), color: uiVar('adm-ink', '#fff'), fontSize: 18, fontWeight: '700', marginBottom: 4 },
  subtitle: { color: 'rgba(255,255,255,0.4)', fontSize: 12, maxWidth: 560, lineHeight: 17 },

  headerActions: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },

  resetBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingVertical: 8, paddingHorizontal: 12, borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.05)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)',
  },
  resetBtnText: { color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: '600' },

  previewToggleBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingVertical: 8, paddingHorizontal: 12, borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.05)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)',
  },
  previewToggleBtnActive: {
    backgroundColor: uiVar('adm-accent-wash-10', 'rgba(167,139,250,0.1)'), borderColor: uiVar('adm-accent-wash-30', 'rgba(167,139,250,0.3)'),
  },
  previewToggleText: { color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: '600' },

  saveBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: '#fff', paddingVertical: 9, paddingHorizontal: 16, borderRadius: 8,
  },
  saveBtnDisabled: { opacity: 0.5 },
  saveBtnText: { color: '#000', fontWeight: '700', fontSize: 13 },

  dirtyBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: 'rgba(245,158,11,0.08)', borderWidth: 1, borderColor: 'rgba(245,158,11,0.2)',
    borderRadius: 8, paddingVertical: 8, paddingHorizontal: 12, marginBottom: 12,
  },
  dirtyText: { color: '#f59e0b', fontSize: 12, fontWeight: '600' },

  tabBar: {
    flexDirection: 'row', gap: 4, marginBottom: 12,
    backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 4,
  },
  tab: {
    flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 6, paddingVertical: 9, borderRadius: 8,
  },
  tabActive: { backgroundColor: uiVar('adm-accent-wash-15', 'rgba(167,139,250,0.15)') },
  tabText: { color: 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: '600' },
  tabTextActive: { color: uiVar('adm-accent', '#a78bfa') },
  tabCharCount: { color: 'rgba(255,255,255,0.2)', fontSize: 10, marginLeft: 2 },

  editorRow: {
    flex: 1, flexDirection: 'row', gap: 12,
    minHeight: 500,
  },
  pane: {
    flex: 1, display: 'flex', flexDirection: 'column',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)',
    borderRadius: 10, overflow: 'hidden',
    backgroundColor: 'rgba(255,255,255,0.02)',
  },
  paneHalf: { flex: 1 },
  paneHeader: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 12, paddingVertical: 8,
    borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.06)',
    backgroundColor: 'rgba(255,255,255,0.02)',
  },
  paneLabel: { color: 'rgba(255,255,255,0.3)', fontSize: 11, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.4 },

  editor: {
    flex: 1,
    padding: 16,
    color: 'rgba(255,255,255,0.85)',
    fontSize: 13,
    lineHeight: 20,
    ...(Platform.OS === 'web' ? {
      fontFamily: 'monospace',
      whiteSpace: 'pre-wrap',
      outlineStyle: 'none',
      minHeight: 460,
      resize: 'none',
    } : {}),
  },

  previewScroll: { flex: 1 },
  previewText: {
    padding: 16,
    color: 'rgba(255,255,255,0.75)',
    fontSize: 13,
    lineHeight: 20,
    ...(Platform.OS === 'web' ? { whiteSpace: 'pre-wrap' } : {}),
  },
});
